<script setup lang="ts">
    import LockIcon from "@/assets/lock_grey.svg";
    import KButton from "@/components/Button.vue";
    import KFormFieldDisplay from "@/components/forms/displays/FormFieldDisplay/KFormFieldDisplay.vue";
    import KSection from "@/components/generic/KSection.vue";
    import KSub from "@/components/forms/subComponents/Sub/KSub.vue";
    import { useStore } from "@/store";
    import { getLockName, NllLock, SparrValue } from "@/store/modules/recept/helpers";
    import SparrForskrivning from "@/tabs/Receptforskrivning/SparrForskrivning.vue";
    import { computed, inject, ref, toRefs, watch } from "vue";
    import { hasPersonnummer } from "@/utils/patient/patientData";

    const store = useStore();

    const patient = computed(() => store.getters["patientData/patient"]);

    const props = defineProps<{
        hasSelectedLakemedel: boolean;
        existingSparr: SparrValue | null;
        receptforskrivningId?: string;
    }>();

    const formRef: any = inject("formRef");

    const { existingSparr, receptforskrivningId } = toRefs(props);
    const showModal = ref(false);
    const selectedSparr = ref<SparrValue | null>(null);
    const receptforskrivning = computed(() =>
        store.getters["receptforskrivning/receptforskrivningById"]({
            id: receptforskrivningId?.value,
        })
    );

    defineExpose({
        formListeners: [
            {
                fieldNames: ["sparr_legal_ground", "sparr_subject_type"],
                action: ({ values }) => {
                    if (values.sparr_legal_ground && values.sparr_subject_type) {
                        selectedSparr.value = {
                            sparr_legal_ground: values.sparr_legal_ground as string,
                            sparr_subject_type: values.sparr_subject_type as string,
                        };

                        return;
                    }

                    selectedSparr.value = null;
                },
            },
        ] as Array<{
            fieldNames: string[];
            action: ({ values }: { values: { [key: string]: unknown } }) => void;
        }>,
    });

    const toggleShowModal = () => {
        showModal.value = !showModal.value;
    };

    const onSubmit = (data: SparrValue) => {
        formRef?.value?.setFormValues?.({
            values: {
                sparr_legal_ground: data.sparr_legal_ground,
                sparr_subject_type: data.sparr_subject_type,
            },
            reset: false,
        });
        toggleShowModal();
    };

    watch(existingSparr, (newValue) => {
        selectedSparr.value = newValue;
    });
</script>

<template>
    <KSection
        :expanded="hasSelectedLakemedel"
        name="sparr"
        heading="Spärr"
        :collapsible="hasSelectedLakemedel"
        :number="5"
    >
        <div class="form-content">
            <KText name="sparr_legal_ground" :hidden="true" :ignoreEmptyData="true" />
            <KText name="sparr_subject_type" :hidden="true" :ignoreEmptyData="true" />
            <template v-if="selectedSparr">
                <KFormFieldDisplay
                    name="sparr"
                    label="Typ av spärr"
                    class="span-all"
                    :value="NllLock[getLockName({ value: selectedSparr })]?.label"
                />
            </template>
            <template v-else>
                <KButton
                    type="button"
                    @click="toggleShowModal"
                    variant="contour-alert"
                    size="slim"
                    :disabled="!hasPersonnummer({ personIdType: patient?.personIdType })"
                >
                    <img :src="LockIcon" alt="Lägg till spärr" />
                    <span>Lägg till spärr</span>
                </KButton>
                <KSub
                    v-if="!hasPersonnummer({ personIdType: patient?.personIdType })"
                    class="span-all"
                    text="Det är inte möjligt att spärra recept för patienter utan personnummer. Förskrivningen kan bara hämtas ut på valt apotek och syns inte för vården eller patienten."
                    type="info-dark"
                />
                <portal v-if="showModal" to="modal">
                    <SparrForskrivning
                        @submit="onSubmit"
                        @cancel="toggleShowModal"
                        :receptforskrivning="receptforskrivning"
                    />
                </portal>
            </template>
        </div>
    </KSection>
</template>

<style lang="scss" scoped>
    .sparr-section {
    }
</style>
